import React, { useEffect,  useRef, useState } from "react";
import { Button, Grid, LinearProgress, Snackbar, TextField, Tooltip, Typography } from "@material-ui/core";
import MUIDataTable from "mui-datatables";


// components
import PageTitle from "../../components/PageTitle";
import Widget from "../../components/Widget";
import Table from "../dashboard/components/Table/Table";
import FixturesToolbar from "./components/FixturesToolbar";
import CopyPredictionsDialog from "./components/CopyPredictionsDialog";

// styles
import useStyles from "./styles";

// data
import { ControlCameraOutlined } from "@material-ui/icons";

//logo
import blinkingGreenDot from "../../images/blinking_green_dot.gif";
import InfoIcon from "@material-ui/icons/Info";

// constants
import { SERVER } from "../../constants";

// context
import { useGroupState } from "../../context/GroupContext";

// localization
import { useTranslation, useLanguage } from "../../localization";

// Luxon
const { DateTime } = require("luxon");

// A match whose result is in (live or finished). These are never copy targets:
// copying predictions only makes sense for upcoming matches, so settled matches
// are made unselectable and skipped if one ever slips through.
function isSettled(fixture) {
  return !!(fixture && (fixture.live || fixture.status === "post"));
}


export default function Fixtures(props) {
  // The selected group determines which set of predictions to load/save.
  // `groups` (each with its `leagues`) is used to offer copy targets.
  var { selectedGroup, groups } = useGroupState();
  var t = useTranslation();
  var { language } = useLanguage();

  // Render the Points cell for a fixture. Points are computed server-side
  // (respecting the group's scoring mode, knockout multiplier and
  // regulation-time scores) and read from the /allinfo payload via
  // `pointsEntry` ({ points, points_breakdown }) for the logged-in user; this
  // component only formats them. A missing entry on a locked, played fixture
  // means the user had no scoreable prediction, so they score 0.
  function renderPoints(fixture, pointsEntry) {
    // Upcoming matches (not started, or not yet locked) have nothing to score.
    if (fixture['status'] == 'pre' || !fixture['locked']) {
      return t.common.tbd;
    }
    var points = pointsEntry ? pointsEntry.points : 0;
    var points_breakdown = pointsEntry ? (pointsEntry.points_breakdown || '') : '';

    if (fixture['live']) {
      return <span className={`${classes.points} ${classes.liveData}`}>
        <Typography>{points}</Typography>
        <img src={blinkingGreenDot} alt="logo" className={classes.blinkingGreenDot} />
        {points > 0 ? <Tooltip placement='bottom-start' title={points_breakdown} leaveDelay={500} leaveTouchDelay={3000}>
          <InfoIcon className={classes.infoIcon} />
        </Tooltip> : ''}
      </span>;
    } else {
      return <span className={classes.points}>
        <Typography>{points}</Typography>
        {points > 0 ? <Tooltip placement='bottom-start' title={points_breakdown} leaveDelay={500} leaveTouchDelay={3000}>
          <InfoIcon className={classes.infoIcon} />
        </Tooltip> : ''}
      </span>;
    }

  }

  function setPrediction(match_id, prediction_type, value) {
    var newPredictions = {...predictionsValue.current};
    
    if (!(match_id in newPredictions)) {
      newPredictions[match_id] = {}
    }
    var prediction_key = prediction_type === 'home' ? 'home_score' : 'away_score';
    if ((value === '' || value === NaN) && prediction_key in newPredictions[match_id]) {
      newPredictions[match_id][prediction_key] = null
    } else {
      newPredictions[match_id][prediction_key] = parseInt(value);
    }
    setPredictions(newPredictions);
  }

  function guardar() {
    const requestOptions = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          'group': selectedGroup,
          'league': props.league,
          'name': localStorage.getItem('name'),
          predictions: predictionsValue.current,
        })
    };
  
    fetch(SERVER + "/predictions", requestOptions)
      .then(result => result.json())
      .then(
        (json) => {
          setGuardarSnackBarOpen(true);
        },
        // Note: it's important to handle errors here
        // instead of a catch() block so that we don't swallow
        // exceptions from actual bugs in components.
        (error) => {
          console.debug(error);
          alert(t.fixtures.couldNotSave)
        },
      )
  }

  function getActualScoreElement(fixture) {
    if (fixture['live']) {
      return <span className={classes.liveData}>{fixture['home_score']}<span> - </span>{fixture['away_score']} <img src={blinkingGreenDot} alt="logo" className={classes.blinkingGreenDot} /> </span>;
    }
    else {
      return fixture['home_score'] !== undefined ? <span>{fixture['home_score']}<span> - </span>{fixture['away_score']}</span> : t.common.tbd;
    }
  }
  
  var classes = useStyles();
  // admin mode (?admin) disables the default "hide Scheduled" filter (via the
  // initial scheduledFilterOn below) and the 10-minute prediction lock.
  var admin_mode = new URLSearchParams(props.location.search).get("admin");
  // Raw fetched payload ({ fixtures, savedPredictions }). Headers/rows are
  // derived from it below so they rebuild when the language (t) changes, not
  // just when the data does.
  const [data, setData] = useState(null);
  const [predictions, setPredictions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [guardarSnackBarOpen, setGuardarSnackBarOpen] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState(0);
  const predictionsValue = useRef(predictions);
  // Match copy-to-group flow: selected table rows (dataIndexes), the dialog, the
  // matches/scores it will copy, and a count of selected matches with no
  // prediction (nothing to copy) so the dialog can report them.
  const [selectedIndexes, setSelectedIndexes] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyItems, setCopyItems] = useState([]);
  const [skippedCount, setSkippedCount] = useState(0);
  const [copiedSnackOpen, setCopiedSnackOpen] = useState(false);
  // Whether the default ['Scheduled'] Status filter is currently active. This is
  // the single source of truth for the filter: the Status column's filterList is
  // driven from it (see the `columns` memo) and it's kept in sync with the
  // table's own filter UI via onFilterChange. The "Hide scheduled" toolbar button
  // only shows once it's been cleared. Starts active unless in ?admin mode.
  const [scheduledFilterOn, setScheduledFilterOn] = useState(!admin_mode);


  useEffect(() => {
      var fixtures;
      var params = {
        'league': props.league,
      }
    var url = new URL(SERVER + '/fixtures')
      url.search = new URLSearchParams(params).toString();
      fetch(url)
      .then((response) => response.json())
      .then((json) => {
        fixtures = json;
        var predictionsUrl = new URL(SERVER + '/predictions')
        predictionsUrl.search = new URLSearchParams({
          'group': selectedGroup,
          'league': props.league,
          'name': localStorage.getItem('name'),
        }).toString();
        var allInfoUrl = new URL(SERVER + '/allinfo')
        allInfoUrl.search = new URLSearchParams({ 'group': selectedGroup }).toString();
        // /predictions carries the raw saved scores used to prefill the
        // editable inputs; /allinfo carries the server-computed points (which
        // respect the group's scoring mode) shown in the Points column.
        return Promise.all([
          fetch(predictionsUrl).then(r => r.json()),
          fetch(allInfoUrl, { cache: "no-store" }).then(r => r.json()),
        ]);
      },
      // Note: it's important to handle errors here
      // instead of a catch() block so that we don't swallow
      // exceptions from actual bugs in components.
      (error) => {
        console.debug(error);
        alert(t.fixtures.couldNotLoad)
      })
      .then(([savedPredictions, allInfo]) => {
          // Store the raw payload; the localized headers and the rendered rows
          // are derived from it (and the current language) in the useMemos below.
          setData({ fixtures: fixtures, savedPredictions: savedPredictions, allInfo: allInfo });
          setIsLoading(false);
        },
        // Note: it's important to handle errors here
        // instead of a catch() block so that we don't swallow
        // exceptions from actual bugs in components.
        (error) => {
          console.debug(error);
          alert(t.fixtures.couldNotGetPredictions)
        }
      )
    // Reload when the selected group changes, since predictions differ per group.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroup]);

  // Column definitions, rebuilt when the language (t) changes so the localized
  // headers update live. mui-datatables caches columns internally, so the table
  // is also remounted via `key={language}` on a language change (see render).
  var headers = React.useMemo(() => [
    {
      'name': 'Date',
      'options': {
        customHeadLabelRender: () => <Typography variant='h5'>{t.fixtures.date}</Typography>,
        filter: false
      }
    },
    {
      'name': 'Teams',
      'options': {
        customHeadLabelRender: () => <Typography variant='h5'>{t.fixtures.teams}</Typography>,
        filter: false
      }
    },
    {
      'name': 'Status',
      'options': {
        customHeadLabelRender: () => <Typography variant='h5'>{t.fixtures.status}</Typography>,
        // filterList is injected by the `columns` memo from `scheduledFilterOn`.
      }
    },
    {
      'name': 'Prediction',
      'options': {
        customHeadLabelRender: () => <Typography variant='h5'>{t.fixtures.prediction}</Typography>,
        filter: false
      }
    },
    {
      'name': 'Actual Score',
      'options': {
        customHeadLabelRender: () => <Typography variant='h5'>{t.fixtures.actualScore}</Typography>,
        filter: false
      }
    },
    {
      'name': 'Points',
      'options': {
        customHeadLabelRender: () => <Typography variant='h5'>{t.fixtures.points}</Typography>,
        filter: false
      }
    },
  ], [t]);

  // Columns handed to the table: `headers` with the Status column's filterList
  // injected from `scheduledFilterOn`. mui-datatables re-applies a column's
  // filterList option on any props change, so this controlled value (kept in
  // sync via onFilterChange) means every re-render re-applies the *current*
  // filter rather than fighting the user's last action. Toggling the state both
  // updates the filter and produces a fresh columns reference so it takes effect.
  var columns = React.useMemo(
    () =>
      headers.map(h =>
        h.name === 'Status'
          ? { ...h, options: { ...h.options, filterList: scheduledFilterOn ? ['Scheduled'] : [] } }
          : h,
      ),
    [headers, scheduledFilterOn],
  );

  // Rendered rows, derived from the fetched payload. Memoized on [data, t] so
  // they stay referentially stable across prediction keystrokes (which only
  // update `predictions`), preserving focus in the uncontrolled inputs, while
  // still rebuilding when the data loads or the language changes.
  var rows = React.useMemo(() => {
    if (!data) {
      return [];
    }
    var fixtures = data.fixtures;
    var savedPredictions = data.savedPredictions;
    // Map fixtureId -> the logged-in user's server-computed points for this
    // league, pulled from /allinfo. /allinfo only carries predictions (and
    // points) for locked, played fixtures, which is exactly when the Points
    // column shows a value.
    var userName = localStorage.getItem('name');
    var userPointsByFixture = {};
    var leagueInfo = data.allInfo &&
      (data.allInfo.leagues || []).find(l => l.name === props.league);
    if (leagueInfo) {
      for (const f of leagueInfo.fixtures || []) {
        let mine = (f.predictions || []).find(
          p => p.name === userName || p.username === userName,
        );
        if (mine) {
          userPointsByFixture[f.id] = {
            points: mine.points,
            points_breakdown: mine.points_breakdown,
          };
        }
      }
    }
    var rows = [];
    for (const fixture of fixtures) {
      let date = DateTime.fromISO(fixture['date'], { zone: 'utc'});
      date = date.toLocal();
      let lock_time = date.minus({ minutes: 10 });
      let now = DateTime.local();
      let is_locked = now >= lock_time && admin_mode === null ? true : false;
      fixture['locked'] = is_locked;

      let teams = <span className={classes.teamNames}>
        <img src={fixture['home_flag_url']} alt="logo" className={classes.teamFlag} />
        <Typography>
          {fixture['home_team']} vs {fixture['away_team']}
        </Typography>
        <img src={fixture['away_flag_url']} alt="logo" className={classes.teamFlag} />
      </span>;

      let status = fixture['status_detail'];
      let home_prediction = '';
      let away_prediction = '';
      // Prefer predictions entered this session (held in the ref) over the
      // load-time server snapshot, then fall back to it. The inputs are
      // uncontrolled (defaultValue), so when this memo recomputes on a language
      // change the table remounts (key={language}) and rebuilds them; reading
      // the ref here keeps in-session predictions from vanishing on that
      // remount. The ref isn't a dep, so keystrokes still don't rebuild rows.
      let typed = predictionsValue.current[fixture['id']] || {};
      let saved = savedPredictions[fixture['id']] || {};
      if (typed['home_score'] !== undefined) {
        home_prediction = typed['home_score']
      } else if (saved['home_score'] !== undefined) {
        home_prediction = saved['home_score']
      }
      if (typed['away_score'] !== undefined) {
        away_prediction = typed['away_score']
      } else if (saved['away_score'] !== undefined) {
        away_prediction = saved['away_score']
      }

      rows.push([
        // Show the kickoff in the viewer's local time zone (date.toLocal()
        // above), rather than the server's baked-in US-timezone date_display.
        date.toFormat('M/d h:mm a'),
        teams,
        status,
        is_locked ?
        <span key={`pred-${fixture['id']}`}>{home_prediction}<span> - </span>{away_prediction}</span>
        :
        <div key={`pred-${fixture['id']}`}>
          <TextField
            type='number'
            defaultValue={home_prediction}
            InputProps={{ inputProps: { min: 0 } }}
            className={classes.predictionInput}
            onChange={e => setPrediction(fixture['id'], 'home', e.target.value)}
          />
          <span className={classes.hyphen}>-</span>
          <TextField
            type='number'
            defaultValue={away_prediction}
            InputProps={{ inputProps: { min: 0 } }}
            className={classes.predictionInput}
            onChange={e => setPrediction(fixture['id'], 'away', e.target.value)}
          />
        </div>,
        <div>{getActualScoreElement(fixture)}</div>,
        renderPoints(fixture, userPointsByFixture[fixture['id']]),
      ])
    }
    return rows;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, t]);


  useEffect(() => {
    let changed = predictions !== predictionsValue.current;
    predictionsValue.current = predictions;
    
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (changed) {
      setTypingTimeout(setTimeout(function () {
        console.log('Auto-guardaring after 4 seconds idle!');
        guardar();
      }, 3000))
    }
  }, [predictions])

  // Groups (other than the current one) that also contain this league — the only
  // valid copy targets. This is the whole "only succeeds if the league exists in
  // the other group too" rule: we never offer a group that lacks the league.
  var targetGroups = (groups || []).filter(
    g => g.name !== selectedGroup && (g.leagues || []).includes(props.league),
  );

  // Build the copy payload from the selected rows and open the dialog. A row's
  // dataIndex maps directly to data.fixtures[dataIndex] (rows are built in that
  // order). The prediction to copy is the in-session value (the ref) merged over
  // the loaded snapshot; matches without a complete prediction are skipped.
  function openCopyDialog() {
    var fixtures = (data && data.fixtures) || [];
    var saved = (data && data.savedPredictions) || {};
    var items = [];
    var skipped = 0;
    selectedIndexes.forEach(idx => {
      var fixture = fixtures[idx];
      // Belt-and-suspenders: settled matches are unselectable, but never copy
      // one even if it somehow ends up in the selection.
      if (!fixture || isSettled(fixture)) {
        return;
      }
      var merged = {
        ...(saved[fixture.id] || {}),
        ...(predictionsValue.current[fixture.id] || {}),
      };
      var h = merged.home_score;
      var a = merged.away_score;
      var complete =
        h !== undefined && h !== null && h !== "" &&
        a !== undefined && a !== null && a !== "";
      if (complete) {
        items.push({
          id: fixture.id,
          fixture,
          home_score: parseInt(h, 10),
          away_score: parseInt(a, 10),
        });
      } else {
        skipped += 1;
      }
    });
    setCopyItems(items);
    setSkippedCount(skipped);
    setCopyOpen(true);
  }

  function handleCopied() {
    setCopyOpen(false);
    setSelectedIndexes([]);
    setCopiedSnackOpen(true);
  }

  // dataIndexes of every selectable (unsettled) match. Backs the toolbar
  // "Select all" toggle, which is the only select-all affordance on mobile,
  // where mui-datatables' stacked layout hides the header checkbox.
  var selectableIndexes = ((data && data.fixtures) || []).reduce(
    (acc, fixture, i) => {
      if (!isSettled(fixture)) {
        acc.push(i);
      }
      return acc;
    },
    [],
  );
  var allSelected =
    selectableIndexes.length > 0 &&
    selectableIndexes.every(i => selectedIndexes.includes(i));

  function toggleSelectAll() {
    setSelectedIndexes(allSelected ? [] : selectableIndexes);
  }

  const onGuardarSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }

    setGuardarSnackBarOpen(false);
  };

  return (
    <>
      <PageTitle title={props.title} />
      <Typography variant="body2" className={classes.tzNote}>
        {t.common.localTimeNote(DateTime.local().toFormat('ZZZZ'))}
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <MUIDataTable
            key={language}
            title=""
            data={rows}
            columns={columns}
            options={
              isLoading ? 
              {
                filterType: "checkbox",
                selectableRows: 'none',
                download: false,
                print: false,
                viewColumns: false,
                pagination: false,
                textLabels: {
                  body: {
                      noMatch: <LinearProgress />,
                  },
                },
              } 
              :
              {
                filterType: "checkbox",
                // Track the Status filter so the "Hide scheduled" button only
                // shows once 'Scheduled' has been cleared. Status is column
                // index 2; filterList is indexed by column.
                onFilterChange: (changedColumn, filterList) => {
                  var statusFilter = filterList[2] || [];
                  setScheduledFilterOn(statusFilter.includes('Scheduled'));
                },
                // Only offer match selection when there's at least one other
                // group (sharing this league) to copy into; otherwise there's
                // nothing to copy to, so hide the checkboxes and toolbar button.
                selectableRows: targetGroups.length > 0 ? 'multiple' : 'none',
                selectableRowsHeader: true,
                // Settled matches (live/finished) are never selectable, so they
                // can't be copied even if the Status filter is changed to reveal
                // them or the page is in ?admin mode. dataIndex -> data.fixtures.
                isRowSelectable: dataIndex => {
                  var fixture = (data && data.fixtures) || [];
                  return !isSettled(fixture[dataIndex]);
                },
                // Controlled selection so a successful copy can clear it.
                rowsSelected: selectedIndexes,
                onRowSelectionChange: (cur, all, rowsSelected) =>
                  setSelectedIndexes(rowsSelected),
                // Always-visible main-toolbar actions:
                // - "Hide scheduled" reinstates the default Status filter after
                //   the user has cleared it via the column filter (bumps the
                //   nonce that rebuilds `columns`). Hidden in ?admin mode, which
                //   ships without the default filter.
                // - "Select all"/"Clear" toggle: the select-all affordance on
                //   mobile, where the stacked layout hides the header checkbox;
                //   it also drives the "copy all unsettled" flow. Only shown when
                //   there's a group to copy into.
                customToolbar: () => (
                  <>
                    {!admin_mode && !scheduledFilterOn && (
                      <Button
                        color="primary"
                        onClick={() => setScheduledFilterOn(true)}
                      >
                        {t.fixtures.hideScheduled}
                      </Button>
                    )}
                    {targetGroups.length > 0 && (
                      <Button
                        color="primary"
                        onClick={toggleSelectAll}
                        disabled={selectableIndexes.length === 0}
                      >
                        {allSelected
                          ? t.fixtures.clearSelection
                          : t.fixtures.selectAll}
                      </Button>
                    )}
                  </>
                ),
                // Render the selection toolbar above the main one instead of
                // replacing it, so the "Select all"/"Clear" toggle stays
                // reachable while matches are selected (needed on mobile).
                selectToolbarPlacement: "above",
                // The "Copy to group…" action shown in the selection toolbar
                // (appears once at least one match is checked).
                customToolbarSelect: () => (
                  <Button
                    color="primary"
                    onClick={openCopyDialog}
                    style={{ marginRight: 24 }}
                  >
                    {t.fixtures.copyToGroup}
                  </Button>
                ),
                download: false,
                print: false,
                viewColumns: false,
                // rowsPerPage: 200,
                pagination: false,
                textLabels: {
                  body: {
                      noMatch: t.fixtures.noFixtures,
                  },
                },
              }
            }
          />
        </Grid>
      </Grid>
      <Snackbar
        open={guardarSnackBarOpen}
        autoHideDuration={3000}
        message={t.fixtures.savedSnackbar}
        onClose={onGuardarSnackbarClose}
      />
      <CopyPredictionsDialog
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        league={props.league}
        userName={localStorage.getItem("name")}
        targetGroups={targetGroups}
        copyItems={copyItems}
        skippedCount={skippedCount}
        onCopied={handleCopied}
      />
      <Snackbar
        open={copiedSnackOpen}
        autoHideDuration={3000}
        message={t.fixtures.copied}
        onClose={() => setCopiedSnackOpen(false)}
      />
    </>
  );
}
